import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Key, Check } from 'lucide-react'
import { supabase } from '../lib/supabase'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [password,  setPassword]  = useState('')
  const [confirm,   setConfirm]   = useState('')
  const [loading,   setLoading]   = useState(false)
  const [done,      setDone]      = useState(false)
  const [error,     setError]     = useState('')

  const handleReset = async () => {
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    setError('')

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password })
      if (updateError) {
        setError(updateError.message)
        return
      }
      setDone(true)
      setTimeout(() => navigate('/home'), 2000)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[440px]">
        <div className="flex justify-center mb-5">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-[16px] bg-forest text-forest-ink">
            <Key className="h-[22px] w-[22px]" strokeWidth={1.6} />
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2 text-center">
          {done ? 'Password updated' : 'Reset password'}
        </div>
        <h1 className="font-serif text-[28px] font-normal leading-[1.1] tracking-[-0.015em] text-ink text-center">
          {done ? (
            <>You're all <em className="italic">set</em>.</>
          ) : (
            <>Choose a new <em className="italic">password</em>.</>
          )}
        </h1>
        <p className="font-serif text-[13px] italic text-ink-muted mt-1.5 mb-8 leading-[1.4] text-center">
          {done ? 'Taking you to your pantry.' : 'Use at least 6 characters.'}
        </p>

        {done && (
          <div className="flex items-center justify-center gap-2 text-forest">
            <Check className="h-5 w-5" strokeWidth={2} />
            <span className="font-serif text-[14px] font-medium italic">Success.</span>
          </div>
        )}

        {!done && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-medium text-ink-muted uppercase tracking-[0.12em]">
                New password
              </label>
              <input
                type="password"
                placeholder="Min. 6 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full rounded-[12px] border border-border-sand bg-cream px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-medium text-ink-muted uppercase tracking-[0.12em]">
                Confirm password
              </label>
              <input
                type="password"
                placeholder="Repeat the password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="w-full rounded-[12px] border border-border-sand bg-cream px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
              />
            </div>

            {error && (
              <p className="rounded-[12px] bg-critical-bg border border-critical/20 text-critical text-[13px] p-3">
                {error}
              </p>
            )}

            <button
              onClick={handleReset}
              disabled={loading}
              className="w-full rounded-[14px] bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em] disabled:opacity-50 transition mt-2"
            >
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
